import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { router } from 'router';

import { useAuth, useChatSession, useConfig } from '@chainlit/react-client';

import ChatSettingsModal from './components/ChatSettings';
import { ThemeProvider } from './components/ThemeProvider';
import { Loader } from '@/components/Loader';
import { Toaster } from '@/components/ui/sonner';

import { userEnvState } from 'state/user';

declare global {
    interface Window {
        cl_shadowRootElement?: HTMLDivElement;
        transports?: string[];
        theme?: {
            light: Record<string, string>;
            dark: Record<string, string>;
        };
    }
}

function App() {
    const { config } = useConfig();

    const { isAuthenticated, data, isReady } = useAuth();
    const userEnv = useRecoilValue(userEnvState);
    const { connect, chatProfile, setChatProfile, idToResume } =
        useChatSession();

    const configLoaded = !!config;

    const chatProfileOk = configLoaded
        ? config.chatProfiles.length
            ? !!chatProfile
            : true
        : false;

    useEffect(() => {
        if (!isAuthenticated || !isReady || !chatProfileOk) {
            return;
        }

        // On thread pages, wait until idToResume matches the URL thread ID before
        // connecting. This prevents a race condition where two connect() calls fire
        // at near-identical debounce times: the first with idToResume="" (wrong) and
        // the second with the correct thread ID. Whichever fires last wins, so the
        // correct socket was intermittently being closed by the stale one.
        const threadMatch =
            window.location.pathname.match(/\/thread\/([^/?]+)/);
        if (threadMatch && idToResume !== decodeURIComponent(threadMatch[1])) {
            return;
        }

        connect({
            transports: window.transports,
            userEnv
        });
    }, [userEnv, isAuthenticated, connect, isReady, chatProfileOk, idToResume]);

    useEffect(() => {
        if (
            !configLoaded ||
            !config ||
            !config.chatProfiles?.length ||
            chatProfile
        ) {
            return;
        }

        const defaultChatProfile = config.chatProfiles.find(
            (profile) => profile.default
        );

        if (defaultChatProfile) {
            setChatProfile(defaultChatProfile.name);
        } else {
            setChatProfile(config.chatProfiles[0].name);
        }
    }, [configLoaded, config, chatProfile, setChatProfile]);

    if (!configLoaded && isAuthenticated && !isEmbeddedSidebar) return null;

    return (
        <ThemeProvider
            storageKey="vite-ui-theme"
            defaultTheme={isEmbeddedSidebar ? 'light' : data?.default_theme}
        >
            <Toaster richColors className="toast" position="bottom-right" />

            <ChatSettingsModal />
            <RouterProvider router={router} />

            <div
                className={cn(
                    'top-0 fixed flex justify-center items-center bg-background p-2 size-full',
                    (isReady || isEmbeddedSidebar) && 'hidden'
                )}
            >
                <Loader className="size-6!" />
            </div>
        </ThemeProvider>
    );
}

export default App;
